import { graphql, fetchQuery, type Environment } from 'react-relay';
import { OPERAND_TYPE_VALUE } from '@atlaskit/jql-ast';
import { expVal } from '@atlassian/jira-feature-experiments';
import type {
	EndCursor,
	AvatarOption,
	OptionResponse,
	SearchTemplate,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import type { FormatMessage, CloudId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { PropsResolver, PropTypes } from '../../../../../../common/types.tsx';
import {
	generateOptions,
	generateErrorOptions,
	fireJqlBuilderBasicErrorAnalytics,
} from '../../../../../../common/utils.tsx';
import type {
	JiraProjectOptions,
	getProjectProps_recentlyUsedProjectsQuery,
} from './__generated__/getProjectProps_recentlyUsedProjectsQuery.graphql';
import type {
	getProjectPropsQuery,
	getProjectPropsQuery$data as projectPropsResolver$data,
	JiraJqlViewContext,
} from './__generated__/getProjectPropsQuery.graphql';
import messages from './messages.tsx';

type Params = {
	fieldId: string;
	propType: PropTypes;
	formatMessage: FormatMessage;
	jql?: string;
	environment: Environment;
	cloudId: CloudId;
	projectOptions?: JiraProjectOptions;
	operandDisplayValues?: string[];
	searchTemplate: SearchTemplate;
	viewContext: JiraJqlViewContext | null;
};

type GetSuggestedParams = {
	environment: Environment;
	cloudId: CloudId;
	formatMessage: FormatMessage;
	projectOptions?: JiraProjectOptions;
};

/*
    On picker search, we also need to know JQL and search parameters
*/
type GetFieldValueParams = {
	environment: Environment;
	cloudId: CloudId;
	lowerCaseFieldId: string;
	jql?: string;
	search: string;
	endCursor: EndCursor;
	projectOptions?: JiraProjectOptions;
	viewContext: JiraJqlViewContext | null;
};

type ProjectNode = NonNullable<
	NonNullable<
		NonNullable<
			NonNullable<
				NonNullable<NonNullable<projectPropsResolver$data['jira']>['jqlBuilder']>['fieldValues']
			>['edges']
		>[number]
	>['node']
>;

export const getOption = (node: ProjectNode, itemType: 'value'): AvatarOption => ({
	label: node.displayName,
	value: node.jqlTerm,
	optionType: 'avatar',
	avatar: node.project?.avatar?.small,
	square: true,
	itemType,
});

const getSuggestedOptions = async ({
	environment,
	cloudId,
	formatMessage,
	projectOptions,
}: GetSuggestedParams): Promise<OptionResponse> => {
	const refinementOptionRecentProjectsGalaxia = expVal(
		'project-terminology-refresh',
		'isEnabled',
		false,
	)
		? messages.refinementOptionRecentProjectsGalaxia
		: messages.refinementOptionRecentProjects;
	const data = await fetchQuery<getProjectProps_recentlyUsedProjectsQuery>(
		environment,
		graphql`
			query getProjectProps_recentlyUsedProjectsQuery(
				$cloudId: ID!
				$projectOptions: JiraProjectOptions
			) {
				jira @required(action: THROW) {
					jqlBuilder(cloudId: $cloudId) {
						recentlyUsedProjects(projectOptions: $projectOptions) {
							edges {
								node {
									jqlTerm
									displayName
									project {
										avatar {
											small
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			cloudId,
			...(projectOptions && { projectOptions }),
		},
		{ fetchPolicy: 'store-or-network' },
	).toPromise();
	if (
		!data?.jira.jqlBuilder?.recentlyUsedProjects ||
		!data?.jira.jqlBuilder?.recentlyUsedProjects.edges
	) {
		return generateOptions([]);
	}

	const { edges: projectEdges } = data?.jira.jqlBuilder?.recentlyUsedProjects;

	return generateOptions([
		{
			label: formatMessage(refinementOptionRecentProjectsGalaxia),
			optionType: 'group',
			options: projectEdges.reduce<Array<AvatarOption>>((options, edges) => {
				if (!edges || !edges.node) {
					return options;
				}
				const { node } = edges;
				options.push(getOption(node, OPERAND_TYPE_VALUE));
				return options;
			}, []),
		},
	]);
};

const getFieldValueOptions = async ({
	environment,
	cloudId,
	lowerCaseFieldId,
	jql,
	search,
	endCursor,
	projectOptions,
	viewContext,
}: GetFieldValueParams): Promise<OptionResponse> => {
	const after = endCursor;

	const data = await fetchQuery<getProjectPropsQuery>(
		environment,
		graphql`
			query getProjectPropsQuery(
				$cloudId: ID!
				$first: Int = 10
				$jqlTerm: String!
				$jql: String!
				$searchString: String!
				$after: String
				$projectOptions: JiraProjectOptions
				$viewContext: JiraJqlViewContext
			) {
				jira @required(action: THROW) {
					jqlBuilder(cloudId: $cloudId) {
						fieldValues(
							first: $first
							jqlTerm: $jqlTerm
							jqlContext: $jql
							searchString: $searchString
							after: $after
							projectOptions: $projectOptions
							viewContext: $viewContext
						) {
							totalCount
							pageInfo {
								endCursor
							}
							edges {
								node {
									jqlTerm
									displayName
									... on JiraJqlProjectFieldValue {
										project {
											avatar {
												small
											}
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			cloudId,
			first: 10,
			jqlTerm: lowerCaseFieldId,
			jql: jql || '',
			searchString: search,
			after,
			...(projectOptions && { projectOptions }),
			viewContext,
		},
		{ fetchPolicy: 'store-or-network' },
	).toPromise();
	if (!data?.jira?.jqlBuilder?.fieldValues?.edges) {
		return generateOptions([]);
	}

	const { edges: fieldValues, totalCount, pageInfo } = data.jira.jqlBuilder.fieldValues;
	return generateOptions(
		fieldValues.reduce<Array<AvatarOption>>((options, edges) => {
			if (!edges || !edges.node) {
				return options;
			}
			const { node } = edges;
			options.push(getOption(node, OPERAND_TYPE_VALUE));
			return options;
		}, []),
		totalCount,
		pageInfo.endCursor || undefined,
	);
};

export const getProjectProps = ({
	fieldId,
	formatMessage,
	jql,
	environment,
	cloudId,
	projectOptions,
	operandDisplayValues,
	searchTemplate,
	viewContext,
}: Params): PropsResolver => ({
	enableFullList: true,
	operandDisplayValues,
	loadOptions: async (search, renderInitialValues, endCursor) => {
		try {
			if (search.length === 0 && renderInitialValues) {
				return await getSuggestedOptions({
					environment,
					cloudId,
					formatMessage,
					projectOptions,
				});
			}
			return await getFieldValueOptions({
				environment,
				cloudId,
				lowerCaseFieldId: fieldId.toLowerCase(),
				jql,
				search,
				endCursor,
				projectOptions,
				viewContext,
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireJqlBuilderBasicErrorAnalytics('projectJqlBuilderBasicFieldValues', searchTemplate, error);
			return generateErrorOptions(error);
		}
	},
});
